// src/pages/seeker/SeekerDashboard.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobAdPublicAPI, seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { LayoutList, LayoutGrid } from 'lucide-react';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return [addr.suburb, addr.state, addr.country].filter(Boolean).join(', ');
};

const ViewToggle = ({ view, setView }) => (
  <div className="view-toggle">
    <button className={`view-toggle-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
      <LayoutList size={13} /> List
    </button>
    <button className={`view-toggle-btn ${view === 'card' ? 'active' : ''}`} onClick={() => setView('card')}>
      <LayoutGrid size={13} /> Cards
    </button>
  </div>
);

export const SeekerDashboard = () => {
  const [profile, setProfile]     = useState(null);
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [jobView, setJobView]     = useState('list');
  const fileRef = useRef();
  const navigate = useNavigate();

  const loadProfile = async () => {
    const res = await seekerAPI.getProfile();
    setProfile(res.data.data);
    return res.data.data;
  };

  useEffect(() => {
    Promise.all([loadProfile(), jobAdPublicAPI.getActive()])
      .then(([, jobsRes]) => setJobs(jobsRes.data.data.ads || []))
      .finally(() => setLoading(false));
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    toast('Uploading CV...');
    try {
      const formData = new FormData();
      formData.append('cv', file);
      await seekerAPI.uploadCV(formData);
      toast.success('CV uploaded! Parsing skills...');
      setTimeout(async () => {
        const p = await loadProfile();
        toast.success(`${p.skills?.length || 0} skills detected`);
        setUploading(false);
      }, 5000);
    } catch {
      toast.error('Upload failed');
      setUploading(false);
    }
  };

  const skillCount = profile?.skills?.length || 0;
  const cvCount    = profile?.cvs?.length || 0;

  const goToJob = (job) => navigate(`/seeker/jobs/${job.id}`, { state: { ad: job } });

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>My Dashboard</h1>
        <p>Manage your profile, CV and job applications</p>
      </div>

      <div className="stat-grid">
        <div className="stat-box"><div className="stat-box-value">{skillCount}</div><div className="stat-box-label">Skills Detected</div></div>
        <div className="stat-box"><div className="stat-box-value">{cvCount}</div><div className="stat-box-label">CVs Uploaded</div></div>
        <div className="stat-box"><div className="stat-box-value">{jobs.length}</div><div className="stat-box-label">Jobs Available</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '16px' }}>

        {/* Left: CV + Skills */}
        <div>
          {/* CV */}
          <div className="section-box" style={{ marginBottom: '12px' }}>
            <div className="section-box-header">My CV</div>
            <div className="section-box-body">
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileChange} />
              {cvCount === 0 ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>Upload your CV to auto-detect skills</p>
                  <button className="btn btn-primary btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
                    {uploading ? 'Uploading...' : '+ Upload CV'}
                  </button>
                </div>
              ) : (
                <div>
                  {profile?.cvs?.map(cv => (
                    <div key={cv.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid var(--border-lt)' }}>
                      <span style={{ fontSize: '16px' }}>📄</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cv.fileName}</div>
                        <div style={{ fontSize: '11px', color: cv.parseStatus === 'COMPLETE' ? 'var(--success)' : 'var(--warning)' }}>
                          {cv.parseStatus === 'COMPLETE' ? '✓ Parsed' : cv.parseStatus}
                        </div>
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-outline btn-sm" style={{ marginTop: '10px', width: '100%' }}
                    onClick={() => fileRef.current.click()} disabled={uploading}>
                    {uploading ? 'Uploading...' : '+ Upload Another'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Skills */}
          <div className="section-box">
            <div className="section-box-header">
              Detected Skills
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>{skillCount}</span>
            </div>
            <div className="section-box-body">
              {skillCount === 0 ? (
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>Upload a CV to detect your skills</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {profile?.skills?.map(s => <span key={s.id} className="tag">{s.skill?.displayName}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Jobs */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)' }}>
              Available Jobs <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({jobs.length})</span>
            </span>
            <ViewToggle view={jobView} setView={setJobView} />
          </div>

          {loading ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : jobs.length === 0 ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>No jobs available</div>
          ) : jobView === 'list' ? (
            <div className="job-list">
              {jobs.map(job => {
                const pos = job.positions?.[0];
                return (
                  <div key={job.id} className="job-row" style={{ cursor: 'pointer' }}
                    onClick={() => goToJob(job)}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <div className="job-row-logo">{job.groupProfile?.profileName?.[0] || 'C'}</div>
                    <div className="job-row-main">
                      <div className="job-row-title">{job.title}</div>
                      <div className="job-row-company">{job.groupProfile?.profileName}</div>
                      <div className="job-row-meta">
                        {pos?.workplace?.address && <span className="tag tag-ghost">{formatAddress(pos.workplace.address)}</span>}
                        {pos?.workArrangement    && <span className="tag tag-ghost">{pos.workArrangement}</span>}
                        {pos?.schedule           && <span className="tag tag-ghost">{pos.schedule.replace('_', ' ')}</span>}
                        {job.positions?.length > 1 && <span className="tag tag-ghost">{job.positions.length} positions</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="job-card-grid">
              {jobs.map(job => {
                const pos = job.positions?.[0];
                return (
                  <div key={job.id} className="job-card" style={{ cursor: 'pointer' }}
                    onClick={() => goToJob(job)}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
                  >
                    <div className="job-card-logo">{job.groupProfile?.profileName?.[0] || 'C'}</div>
                    <div className="job-card-title">{job.title}</div>
                    <div className="job-card-company">{job.groupProfile?.profileName}</div>
                    <div className="job-card-meta">
                      {pos?.workplace?.address && <span className="tag tag-ghost">{formatAddress(pos.workplace.address)}</span>}
                      {pos?.workArrangement    && <span className="tag tag-ghost">{pos.workArrangement}</span>}
                      {pos?.schedule           && <span className="tag tag-ghost">{pos.schedule.replace('_', ' ')}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
